'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import debounce from 'lodash.debounce';
import { useTranslation } from 'next-i18next';

import { useLocale } from '@hooks/useLocale';
import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import useScrollLock from '@hooks/useScrollLock';
import useVariantSelection from '@adapters/bigcommerce/hooks/product/useVariantSelection';
import { resolveVariant } from '@adapters/bigcommerce/hooks/product/variantAvailability';
import type {
  ColourOption,
  SizeOption,
} from '@adapters/bigcommerce/hooks/product/variantAvailability';
import { useQuickAddToCart } from '@adapters/bigcommerce/hooks/cart/useQuickAddToCart';
import {
  AvailabilityStatus,
  type QuickAddProduct,
} from '@adapters/bigcommerce/types/product';
import { trackQuickAddClick } from '@utils/gtm/ga4/trackQuickAddClick';

import QuickAddPopover from './QuickAddPopover';
import type {
  QuickAddPopoverLayout,
  QuickAddPopoverProductSummaryProps,
  QuickAddStackedVariant,
} from './QuickAddPopover';
import styles from './QuickAdd.module.scss';

export interface QuickAddProps {
  /** `hit.parentSku` — the code accepted by `/api/product/[code]`. */
  productCode: string;
  /** Product title, for the trigger's accessible name. */
  productTitle: string;
  /** PDP URL — fallback link for error/gift-card/two-axis cases. */
  productUrl: string;
  initialColour?: {
    colourName?: string;
    colourSku?: string;
  };
  analytics?: {
    queryID?: string;
    /**
     * Overrides this component's default `source`/`itemListName` derivation
     * (URL-sniffed `'plp'`/`'search'`, page URL) for a host that isn't a
     * PLP/search grid card — currently just `ImageHotspot`'s tile (ED-447),
     * whose add should attribute to the Hotspot banner, not whatever
     * CIP/CLP page it happens to render on. Omit for the existing PLP/search
     * behaviour.
     */
    source?: 'hotspot' | 'pdp';
    itemListName?: string;
    coOpBrand?: string | null;
  };
  /**
   * Fired instead of the default `click_quick_atc` GA4 event when the
   * trigger opens this popover — for a host whose own `user_interaction`
   * click event needs fields this component has no way to know (e.g.
   * ImageHotspot's `click_hotspot_quick_atc`, ED-447, which needs
   * `tab_name`/`coop_brand`/`index`). The caller already has everything it
   * needs in its own closure, so this takes no arguments.
   */
  onTriggerOpen?: () => void;
  /** Pre-hydrated product — skips the on-open fetch (PDP reuse, stories). */
  product?: QuickAddProduct | null;
  /**
   * Algolia hint for the active colour's size-variant count, used only to
   * shape the loading placeholder while the fetch is in flight:
   * - `1` → a single size that auto-adds with no size choice, so show the
   *   "adding" spinner instead of a size grid that would just flash.
   * - `> 1` → render that many skeleton cells so the placeholder matches the
   *   size grid that's about to arrive, instead of a fixed guess.
   * - `undefined` (colour missing the hint) → the default skeleton.
   * Never gates the add itself; the fetched product stays authoritative.
   */
  variantCount?: number;
  /**
   * Forces the compact icon trigger (`QuickAddTrigger`'s
   * `iconVariant="inline"` face — primary/xl, sized to fill its host rather
   * than pinned to an image corner) at every breakpoint, for hosts too
   * small for the desktop hover-CTA text button to read well (e.g.
   * `ProductTile`'s hotspot card, Figma node 427:7198) — not used by the
   * PLP grid card, which keeps the real per-breakpoint trigger face.
   *
   * The popover body still varies by viewport: a narrow viewport gets the
   * same bottom sheet the PLP grid card uses there, but a non-narrow one
   * gets its own `'stacked'` layout (Figma 801:83103) — a rounded card
   * anchored below `stackedAnchorRef` rather than the floating panel the
   * PLP grid card's real desktop layout overlays on its image, since a
   * compact host has no image to overlay.
   */
  compact?: boolean;
  /**
   * Required when `compact` is used on a non-narrow viewport: the element
   * the `'stacked'` popover card anchors below and matches the width of
   * (`ImageHotspot/ProductTile` passes a ref to its whole `.tile` row, not
   * just this component's own 56px slot — see `QuickAddPopover`'s `layout`
   * doc). Unused for the bottom sheet (narrow viewport) or the PLP grid
   * card's real desktop panel (non-compact).
   */
  stackedAnchorRef?: React.RefObject<HTMLElement | null>;
  /**
   * Which `compact` host this is (`QuickAddStackedVariant`): `'tile'`
   * (default, `ImageHotspot`) or `'chip'` (`WhyChoose`, Figma 867:192082) —
   * the chip gets Figma's 48px "With Image" trigger face and a 405px card
   * laid over the chip rather than below it.
   */
  compactVariant?: QuickAddStackedVariant;
  /**
   * Image and price for the popover's own product summary (Figma 372:6981),
   * shown above the size grid — for a host whose trigger doesn't already
   * show them. Also shown in the mobile `'sheet'` (ED-340 QA, beyond the
   * Figma). The name is `productTitle`.
   */
  summary?: Omit<QuickAddPopoverProductSummaryProps, 'name'>;
  /**
   * Notified on every committed open/close (after any blocked-dismiss check
   * below) — for a caller that itself renders inside another floating
   * element and needs to know this popover is open. `ProductTile`'s
   * `HotspotPopover` host uses this to keep its own outer popover from
   * closing when an outside-press/focus-out inside this one's portaled
   * content (outside the outer's own DOM subtree) would otherwise read as
   * "outside" the outer too. Purely observational — never gates the add.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Notified whenever this popover's `view` enters or leaves `'spinner'` —
   * i.e. whether an add-to-cart request is currently in flight (auto-add or
   * a manually-selected size). `ImageHotspot`'s `HotspotPopover` host uses
   * this to keep its own outer popover pinned open — even against a click on
   * a *different* hotspot dot, which an outside-press/focus-out check alone
   * wouldn't catch — while a request is in flight, so it's never abandoned
   * mid-add. Purely observational — never gates the add itself.
   */
  onPendingChange?: (pending: boolean) => void;
  /**
   * Notified with the resolved colour's name whenever it changes — including
   * the initial resolution and every subsequent swatch click in this
   * popover's own colour picker. This popover has no image/price display of
   * its own (it overlays/anchors to the host's), so a host whose own display
   * doesn't otherwise track colour selection (`ImageHotspot`'s `ProductTile`,
   * ED-345 QA) uses this to sync its displayed image/price to match. Purely
   * observational — never gates the add itself.
   */
  onColourChange?: (colourName: string) => void;
}

type QuickAddView = 'skeleton' | 'spinner' | 'sizes' | 'fallback' | 'success';

/** Success bar lifetime before the popover closes itself (Figma timeline). */
const SUCCESS_AUTO_DISMISS_MS = 4000;
/** Grace period after the pointer leaves the card before the popover closes
    — cancelled if the pointer comes back within the window. */
const POINTER_LEAVE_CLOSE_DELAY_MS = 4000;

/**
 * Quick Add on the PLP product card (ED-322). Owns variant resolution
 * (lazy, on open) and the add-to-cart gesture, and composes the
 * `QuickAddPopover` compound parts. Success renders inline — the cart
 * flyout never opens.
 *
 * Products with at most one size need no selection (per Figma): opening
 * the popover adds immediately and shows the success bar.
 */
const QuickAdd = ({
  productCode,
  productTitle,
  productUrl,
  initialColour,
  analytics,
  onTriggerOpen,
  product: providedProduct,
  variantCount,
  compact = false,
  stackedAnchorRef,
  compactVariant = 'tile',
  summary,
  onOpenChange: onOpenChangeProp,
  onPendingChange: onPendingChangeProp,
  onColourChange,
}: QuickAddProps) => {
  const { t } = useTranslation('quickAdd');
  const locale = useLocale();
  // Matches the card grid's own breakpoint (ProductCard's image aspect-ratio
  // switch, `respond(tablet-xl)`) — below it, every layout gets the same
  // bottom sheet, not just under the old 480px mobile cutoff. `useBreakpoint`
  // is still called unconditionally (rules-of-hooks) even when `compact`
  // already decides part of the result below.
  const isNarrowViewport = useBreakpoint(Breakpoints.tabletXl);
  // Drives the popover's body (see `QuickAddPopoverLayout`'s doc) — a
  // narrow viewport always gets the sheet regardless of `compact`; above
  // that, `compact` picks the stacked card (see its prop doc) over the PLP
  // grid card's real desktop panel.
  const layout: QuickAddPopoverLayout = isNarrowViewport
    ? 'sheet'
    : compact
    ? 'stacked'
    : 'desktop';
  // Trigger face + scroll-lock/pointer-leave/prefetch gating below still
  // key off "is this the always-visible icon face", which both the sheet
  // and the stacked layout share.
  const isMobileLayout = layout !== 'desktop';
  const [open, setOpen] = useState(false);
  const [autoAddBlocked, setAutoAddBlocked] = useState(false);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // The desktop panel's max-height (`--card-available-height` in
  // QuickAddPopover) needs the room above the anchor within the card, not
  // floating-ui's viewport-relative `--available-height` — that one is
  // recomputed on scroll and visibly lagged the panel behind the page as it
  // scrolled. Derived from the anchor's own rect (not just the card's full
  // height): the panel's bottom edge lands on the anchor's bottom edge
  // (`side="top"` + `sideOffset={-TRIGGER_HEIGHT}` in QuickAddPopover), so
  // the room actually available above it is `anchor.bottom - card.top`,
  // not the card's full height — using the full height let the panel grow
  // past the anchor's own bottom inset and poke out above the image.
  //
  // Measured on open plus window resize, rather than via a live
  // ResizeObserver: with up to 52 unvirtualized cards per PLP/search page,
  // an observer per card would sit idle for all but the one that's open.
  // The resize listener is scoped the same way — only attached while this
  // card's popover is actually open — so it doesn't carry that same cost.
  // Debounced (same 200ms as `useBreakpoint`'s own resize listener) since a
  // drag-resize fires the event continuously, and each call forces a layout
  // via `getBoundingClientRect`. `useLayoutEffect` so the initial,
  // un-debounced measurement is ready before paint.
  useLayoutEffect(() => {
    if (isMobileLayout || !open) return undefined;
    const root = rootRef.current;
    const anchor = anchorRef.current;
    if (!root || !anchor) return undefined;

    const measure = () => {
      const rootRect = root.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      setAvailableHeight(anchorRect.bottom - rootRect.top);
    };

    measure();
    const debouncedMeasure = debounce(measure, 200);
    window.addEventListener('resize', debouncedMeasure);
    return () => {
      window.removeEventListener('resize', debouncedMeasure);
      debouncedMeasure.cancel();
    };
  }, [isMobileLayout, open]);

  const {
    status,
    product,
    colours,
    sizes,
    selectedColourId,
    selectedSizeId,
    selectedVariant,
    selectedAvailability,
    selectColour,
    selectSize,
    canAddToCart,
    refresh,
    prefetch,
  } = useVariantSelection({
    productCode,
    enabled: open,
    initialColour,
    product: providedProduct,
  });

  const {
    addToCart,
    status: addStatus,
    error: addError,
    errorContext: addErrorContext,
    reset: resetAdd,
  } = useQuickAddToCart();

  // ED-345 QA: reports the resolved colour by name (not just on a swatch
  // click, but its initial resolution too, since a host may not have seen
  // this colour before this popover ever opened) so a host with no
  // image/price of its own can keep its own display in sync.
  useEffect(() => {
    if (selectedColourId == null) return;
    const colour = colours.find((option) => option.id === selectedColourId);
    if (colour) onColourChange?.(colour.name);
  }, [selectedColourId, colours, onColourChange]);

  // Mirrors the render below's `view`, read by callbacks/listeners that are
  // bound once per open/close (the pointermove listener and `handleOpenChange`)
  // rather than re-created on every view change.
  const viewRef = useRef<QuickAddView>('skeleton');

  const handleOpenChange = useCallback(
    (nextOpen: boolean, details?: { reason?: string; cancel: () => void }) => {
      // Once an add is committed the popover must not be dismissed out from
      // under the shopper. The spinner is fully locked — it has to stay until
      // the request resolves and the success/error message replaces it, so even
      // Escape can't cut it short. The success bar blocks only incidental "away"
      // dismissals (an outside click, or focus leaving); the X, Escape and its
      // 4s timer still close it. The X / auto-dismiss / pointer-leave paths call
      // with no `details`, so they are never caught here.
      if (!nextOpen && details) {
        const { reason } = details;
        const awayDismiss =
          reason === 'outside-press' || reason === 'focus-out';
        const blocked =
          viewRef.current === 'spinner'
            ? awayDismiss || reason === 'escape-key'
            : viewRef.current === 'success'
            ? awayDismiss
            : false;
        if (blocked) {
          details.cancel();
          return;
        }
      }
      setOpen(nextOpen);
      onOpenChangeProp?.(nextOpen);
      // Reset on OPEN, not close: the popover stays mounted while its exit
      // animation plays, so resetting on close would flash the panel view
      // over the success bar during the slide-out.
      if (nextOpen) {
        resetAdd();
        setAutoAddBlocked(false);
        // ED-461: the trigger click is the only path that opens the popover
        // (Base UI opens on click, not hover/focus), so this is the
        // `click_quick_atc` interaction. PLP URL captured at click time —
        // path + query, no origin — to mirror ED-460's `item_list_name`.
        // `onTriggerOpen` (ED-447) lets a host with its own richer click
        // event (ImageHotspot's `click_hotspot_quick_atc`) fire that
        // instead of this default.
        if (onTriggerOpen) {
          onTriggerOpen();
        } else {
          trackQuickAddClick({
            linkText: productTitle,
            linkUrl: productUrl,
            interactionLocation: `${window.location.pathname}${window.location.search}`,
          });
        }
      }
    },
    [resetAdd, productTitle, productUrl, onOpenChangeProp, onTriggerOpen]
  );

  // Programmatic closes — the success auto-dismiss timer and the pointer-leave
  // timer — fire from a bare `setTimeout` with no DOM event for Base UI to key
  // off of, so Base UI can't restore focus and it would strand on
  // `document.body` (the success transition unmounts whatever was focused).
  // Restore it ourselves. The X/Escape/outside-press closes are handled by
  // `closeFromButton` / Base UI's own finalFocus instead (see below).
  //
  // `preventScroll` is essential: the trigger is absolutely positioned at the
  // bottom of `.root`, which is `overflow: hidden` (a scroll container). Without
  // it the browser scrolls `.root` to bring the just-focused trigger into view,
  // shifting everything inside it — the overlay visibly jumps up — as the popup
  // slides out. Only this path focuses (button closes let Base UI do it), which
  // is why the jump was auto-dismiss-only.
  const close = useCallback(() => {
    handleOpenChange(false);
    triggerRef.current?.focus({ preventScroll: true });
  }, [handleOpenChange]);

  // Button-driven closes (the success/header X) originate from inside the
  // popup, where Base UI restores focus to the trigger for us (its default
  // finalFocus, same as Escape/outside-press). We must NOT also focus the
  // trigger ourselves here: doing it synchronously moves focus out of the
  // still-open popup in the same tick as the close, which interrupts Base UI's
  // exit transition — the popup snaps toward its closed frame and only then
  // animates, reading as a jump. Letting Base UI own the close keeps the same
  // smooth slide-out the auto-dismiss path already gets.
  const closeFromButton = useCallback(
    () => handleOpenChange(false),
    [handleOpenChange]
  );

  // Desktop only: the CTA hides itself when the pointer leaves the card
  // (`article:hover` in QuickAddTrigger's SCSS); an open popover needs the
  // same behaviour once the shopper's attention moves to another card —
  // but only while there's still a size choice to abandon. Once an add is
  // in flight or has succeeded, the shopper has already committed; closing
  // on a stray mouse-out would cut off the spinner or the success message.
  //
  // Tracks the pointer's position against the card's rect instead of a
  // native `mouseleave` on the card: the panel is portaled and overlaps
  // the trigger (`sideOffset={-TRIGGER_HEIGHT}` below), so the moment it
  // mounts it covers the cursor with an element outside the card's DOM
  // subtree. The browser's next hit-test then sees the cursor as having
  // left the card and fires a real `mouseleave`, closing the popover
  // before the shopper sees it.
  //
  // The close itself is delayed (`POINTER_LEAVE_CLOSE_DELAY_MS`) rather
  // than immediate, so a shopper who overshoots the card and comes right
  // back doesn't lose their in-progress selection — the timer is cancelled
  // the moment the pointer re-enters, and re-checked (not just re-armed)
  // at fire time in case a commit happened while it was pending.
  useEffect(() => {
    if (isMobileLayout || !open) return undefined;
    const card = rootRef.current?.closest('article');
    if (!card) return undefined;

    let closeTimer: number | null = null;
    const cancelClose = () => {
      if (closeTimer == null) return;
      window.clearTimeout(closeTimer);
      closeTimer = null;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (viewRef.current !== 'sizes') {
        cancelClose();
        return;
      }
      const { left, right, top, bottom } = card.getBoundingClientRect();
      const insideCard =
        event.clientX >= left &&
        event.clientX <= right &&
        event.clientY >= top &&
        event.clientY <= bottom;
      if (insideCard) {
        cancelClose();
      } else if (closeTimer == null) {
        closeTimer = window.setTimeout(() => {
          closeTimer = null;
          if (viewRef.current === 'sizes') close();
        }, POINTER_LEAVE_CLOSE_DELAY_MS);
      }
    };

    document.addEventListener('pointermove', handlePointerMove);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      cancelClose();
    };
  }, [isMobileLayout, open, close]);

  // Desktop only: warm the product fetch on hover/focus intent so the size
  // grid is already resolved when the popover opens, hiding the on-open
  // round-trip. A short dwell (matching the CTA's 400ms reveal is overkill —
  // 120ms is enough to mean intent) gates it so a mouse sweeping across the
  // grid of cards doesn't fire a fetch per card; `prefetch` itself is a no-op
  // after the first hit. Keyboard focus prefetches immediately (no sweep to
  // guard against). Mobile has no hover, and tapping opens directly, so it's
  // excluded.
  useEffect(() => {
    if (isMobileLayout) return undefined;
    const card = rootRef.current?.closest('article');
    if (!card) return undefined;

    let dwellTimer: number | null = null;
    const clearDwell = () => {
      if (dwellTimer == null) return;
      window.clearTimeout(dwellTimer);
      dwellTimer = null;
    };
    const onPointerEnter = () => {
      dwellTimer = window.setTimeout(() => {
        dwellTimer = null;
        prefetch();
      }, 120);
    };
    const onFocusIn = () => prefetch();

    card.addEventListener('pointerenter', onPointerEnter);
    card.addEventListener('pointerleave', clearDwell);
    card.addEventListener('focusin', onFocusIn);
    return () => {
      card.removeEventListener('pointerenter', onPointerEnter);
      card.removeEventListener('pointerleave', clearDwell);
      card.removeEventListener('focusin', onFocusIn);
      clearDwell();
    };
  }, [isMobileLayout, prefetch]);

  /**
   * Dispatches the add; resolves `false` when a guard prevented dispatch.
   * `colourIdOverride` (ED-355) is for a colour-row click that must add
   * immediately (a colour-only product with no size dimension at all,
   * `handleColourSelect` below): `selectColour` just called a moment earlier
   * hasn't landed in state yet by the time this runs in the same handler, so
   * reading the closure's own `selectedColourId` here would resolve against
   * the *previous* colour instead of the one just clicked. Omitted (the
   * common path, from `handleSizeSelect`), this falls back to the closure's
   * `selectedColourId` exactly as before.
   */
  const attemptAdd = useCallback(
    async (
      sizeId: number | null,
      colourIdOverride?: number
    ): Promise<boolean> => {
      const colourId = colourIdOverride ?? selectedColourId;
      if (!product || colourId == null || addStatus === 'adding') {
        return false;
      }
      const variant = resolveVariant(product, colourId, sizeId);
      const colour = colours.find((c) => c.id === colourId);
      if (!variant?.entityId || !colour) {
        return false;
      }
      const size =
        sizeId != null ? sizes.find((s) => s.id === sizeId) : undefined;
      // Defense-in-depth: `SelectionCell`'s `disabled` attribute is the only
      // thing stopping a manual click on an unavailable/call-to-order-only
      // size today — re-verify here too, in case stock changes between
      // render and click, or this is ever invoked another way.
      if (
        sizeId != null &&
        (!size ||
          !size.isAvailableForSelectedColour ||
          !size.isPurchasableOnline)
      ) {
        return false;
      }

      const result = await addToCart({
        product,
        variant,
        colour: { id: colour.id, name: colour.name },
        size: size ? { id: size.id, name: size.name } : undefined,
        analytics: {
          // `analytics.source`/`itemListName` (ED-447) override the default
          // PLP/search derivation below for a host like ImageHotspot that
          // isn't a PLP grid card at all — mirrors `getList()` in
          // plpAndSearchDataLayer for every other caller.
          source:
            analytics?.source ??
            (window.location.href.includes('/search') ? 'search' : 'plp'),
          queryID: analytics?.queryID,
          categories: product.categories,
          // Originating PLP URL for GA4 revenue attribution (item_list_name,
          // ED-460). Path + query, no origin — captured at fire time so it
          // reflects the exact list (category/franchise/filtered) the shopper
          // added from, across all PLP types. Only for the PLP/search
          // default: a host that sets its own `source` isn't a PLP, so it
          // gets its own `itemListName` or none.
          itemListName:
            analytics?.itemListName ??
            (analytics?.source
              ? undefined
              : `${window.location.pathname}${window.location.search}`),
          coOpBrand: analytics?.coOpBrand,
        },
      });

      if (!result.ok) {
        // Re-fetch stock so cells reflect what actually failed (e.g. 422 OOS).
        refresh();
      }
      return true;
    },
    [
      product,
      selectedColourId,
      addStatus,
      colours,
      sizes,
      addToCart,
      analytics?.queryID,
      analytics?.source,
      analytics?.itemListName,
      analytics?.coOpBrand,
      refresh,
    ]
  );

  const handleSizeSelect = useCallback(
    (size: SizeOption) => {
      if (addStatus === 'adding') return;
      selectSize(size.id);
      void attemptAdd(size.id);
    },
    [addStatus, selectSize, attemptAdd]
  );

  /**
   * Colour swatch click (ED-355). For a product with a size dimension, this
   * only re-filters the size grid — `selectColour` already clears an
   * incompatible selected size. For a product with none at all (`sizes` is
   * always `[]` regardless of colour, per `getSizeOptionsForColour`'s
   * product-level early return), there is no separate size step left to take
   * — the swatch click both picks the colour and fires the add, mirroring
   * `handleSizeSelect`'s own select-then-add pairing. `attemptAdd`'s
   * `colourIdOverride` is required here, not optional: `selectColour` above
   * hasn't landed in state by the time `attemptAdd` runs in this same
   * handler, so resolving against the closure's own `selectedColourId` would
   * add the *previous* colour instead of the one just clicked.
   */
  const handleColourSelect = useCallback(
    (colour: ColourOption) => {
      if (addStatus === 'adding') return;
      selectColour(colour.id);
      if (sizes.length === 0) {
        void attemptAdd(null, colour.id);
      }
    },
    [addStatus, selectColour, sizes, attemptAdd]
  );

  // With zero or one size there is no choice to make — "Add to Cart" adds
  // directly without any intermediate steps (Figma). Count only the sizes that
  // exist for the SELECTED COLOUR, not the full cross-colour list: a product
  // can offer several sizes overall while a given colour comes in just one
  // (e.g. this colour only in "Short"), and the size grid already filters to
  // `existsForSelectedColour` — so counting the full list would show a grid
  // with a single cell instead of auto-adding that lone size.
  const sizesForColour = sizes.filter((size) => size.existsForSelectedColour);
  const needsSizeSelection = sizesForColour.length > 1;
  const autoSize: SizeOption | undefined =
    sizesForColour.length === 1 ? sizesForColour[0] : undefined;
  // ED-355: a second colour is also a choice left to make, even when the
  // resolved colour's own size count would otherwise auto-add — auto-add is
  // only for "nothing left to choose".
  const needsColourSelection = colours.length > 1;
  // ED-355: the heading names whichever dimensions still need a choice — a
  // single-colour product (or the NO_COLOUR placeholder) leaves the original
  // "Select Size" heading untouched. A colour-only product (no size
  // dimension at all, `sizes` is `[]` regardless of colour — see
  // `handleColourSelect`'s doc) says just "Select Colour" rather than
  // implying a size step that doesn't exist.
  const headerTitle = !needsColourSelection
    ? undefined
    : sizes.length > 0
    ? t('quickAdd.selectColourAndSize')
    : t('quickAdd.selectColour');
  const isAutoAdd =
    status === 'ready' && !needsSizeSelection && !needsColourSelection;
  const autoAddEligible =
    isAutoAdd &&
    product?.availabilityStatus !== AvailabilityStatus.unavailable &&
    (autoSize
      ? autoSize.isAvailableForSelectedColour && autoSize.isPurchasableOnline
      : canAddToCart);

  // In stock but not purchasable online (BigCommerce's `available_online_purchase`
  // false — call-to-order only, per the PDP's `shippingRestrictions` message)
  // is a different situation from genuinely out of stock, and shouldn't show
  // the same "Out of stock" copy.
  const isNotPurchasableOnline = autoSize
    ? autoSize.isAvailableForSelectedColour && !autoSize.isPurchasableOnline
    : !!selectedVariant?.hasStockAvailable && !selectedAvailability;

  const autoAddAttemptedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoAddAttemptedRef.current = false;
      return;
    }
    if (
      !isAutoAdd ||
      autoAddAttemptedRef.current ||
      !product ||
      product.isGiftCard ||
      product.sizeSelectionMerchandising ||
      !autoAddEligible
    ) {
      return;
    }
    autoAddAttemptedRef.current = true;
    void attemptAdd(autoSize?.id ?? null).then((dispatched) => {
      // Never leave the shopper on a spinner: a guard failure (e.g. the
      // initial colour could not be resolved) falls back to the PDP link.
      if (!dispatched) setAutoAddBlocked(true);
    });
  }, [open, isAutoAdd, autoAddEligible, product, autoSize, attemptAdd]);

  const showSuccess = addStatus === 'success';

  // Auto-dismiss: the success bar persists 4s, then the popover closes
  // (unless the shopper closed it first via the X — `open` flips and the
  // cleanup cancels the timer).
  useEffect(() => {
    if (!open || !showSuccess) return undefined;
    const timer = window.setTimeout(close, SUCCESS_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [open, showSuccess, close]);

  const needsPdp =
    status === 'error' ||
    !!product?.isGiftCard ||
    !!product?.sizeSelectionMerchandising ||
    autoAddBlocked ||
    // No-selection product that can't be added (out of stock / unavailable),
    // or whose add failed — there is no grid to fall back to.
    (isAutoAdd &&
      !showSuccess &&
      (!autoAddEligible || addStatus === 'error')) ||
    // ED-355: a multi-colour, no-size-choice product (the shape that would
    // have auto-added if Task 2 hadn't excluded every multi-colour product
    // from isAutoAdd) is the same "no grid to fall back to" situation as the
    // clause above, just keyed on every colour's own stock instead of the
    // single resolved one — an OOS *initial* colour with an in-stock sibling
    // must NOT land here (the colour row still has something to offer).
    (needsColourSelection &&
      !needsSizeSelection &&
      !showSuccess &&
      colours.every((c) => !c.hasAnyStock));

  let view: QuickAddView;
  if (showSuccess) {
    view = 'success';
  } else if (needsPdp) {
    view = 'fallback';
  } else if (status === 'idle' || status === 'loading') {
    // A single-variant product (index hint) will auto-add with no size choice,
    // so show the "adding" spinner during the fetch instead of a size-grid
    // skeleton that would only flash before the auto-add spinner replaces it.
    // Any other count (multi-variant, or the hint absent) keeps the skeleton —
    // we don't yet know whether a size grid is coming.
    view = variantCount === 1 ? 'spinner' : 'skeleton';
  } else if (isAutoAdd || addStatus === 'adding') {
    // Auto-add in flight, or a manually-selected size's add-to-cart request
    // is in flight — either way there's nothing left to select, so the
    // spinner replaces the grid instead of sitting under a busy grid.
    view = 'spinner';
  } else {
    view = 'sizes';
  }
  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  // `onPendingChangeProp` isn't a dependency here on purpose: the caller
  // (`ImageHotspot/index.tsx`) passes a fresh inline callback on every
  // render, so depending on it directly would re-fire this effect — and
  // hence call the callback, and hence (there) set new state — on every
  // render, not just on a real `view` transition. Read the latest one from
  // a ref instead, kept current without needing its own effect.
  const onPendingChangeRef = useRef(onPendingChangeProp);
  onPendingChangeRef.current = onPendingChangeProp;
  useEffect(() => {
    onPendingChangeRef.current?.(view === 'spinner');
  }, [view]);

  const fallbackMessage =
    view !== 'fallback'
      ? null
      : isAutoAdd && !autoAddEligible && !addError
      ? isNotPurchasableOnline
        ? 'callToPurchase'
        : 'oos'
      : addError === 'quickAdd.error.insufficientStock'
      ? 'insufficientStock'
      : status === 'error' || addError || autoAddBlocked
      ? 'error'
      : null;

  // Mirrors the PDP's exact per-product copy (`ProductBuyZone`'s `restrictions`)
  // rather than a generic message, so Quick Add never contradicts the PDP.
  const restrictionMessage =
    fallbackMessage === 'callToPurchase'
      ? (locale === 'fr'
          ? product?.shippingRestrictions?.message_fr
          : product?.shippingRestrictions?.message_en) || null
      : null;

  const titleId = `quick-add-title-${productCode}`;
  const successLabelId = `quick-add-success-${productCode}`;

  // ImageHotspot's compact/stacked Quick Add (Figma 867:163554) closes its
  // size-selector card entirely while an add is in flight, rather than
  // showing a spinner inside it — the trigger's own icon becomes the spinner
  // instead (see `QuickAddTrigger`'s `pending` prop). The PLP grid's real
  // desktop/sheet layouts keep their original behaviour: the panel stays
  // open and crossfades to a spinner surface, matching *that* Figma spec
  // (372:4915). `open` itself (the session being active) is untouched by
  // this — `useVariantSelection`'s `enabled` above still keys off it, so the
  // fetched product survives this closed blip instead of being torn down
  // and re-fetched when the card reopens for the result.
  const isPendingCollapse = compact && view === 'spinner';
  const popoverOpen = isPendingCollapse ? false : open;

  // `QuickAddPopover.Root` passes `modal={layout === 'sheet'}` to Base UI's
  // `Popover.Root`, whose own scroll lock explicitly excludes touch-opened
  // popovers — a real tap on the trigger *is* that, so the page behind the
  // sheet kept scrolling without this. Only the sheet covers the viewport —
  // the stacked card doesn't, so it has nothing to lock scroll against.
  // Keyed on `popoverOpen`, not `open`: while collapsed for a pending add
  // there's no sheet on screen to lock scroll against either.
  useScrollLock(layout === 'sheet' && popoverOpen);

  // Each value gets its own `AnimatePresence` key in `QuickAddPopover.Content`
  // (see its own doc) — skeleton and sizes deliberately share `'panel'` (an
  // instant content swap is expected to be rare once the caller preloads
  // data, and already reads smoothly since the skeleton is sized to match
  // the grid it's standing in for). The terminal fallback gets its own key so
  // reopening onto it after a spinner slides in (Figma 867:163554 step 4/6).
  // `'spinner'` only gets its own key for the non-compact PLP grid — compact
  // never shows it in-card at all (`isPendingCollapse` above closes the
  // whole popup instead), so it falls back to `'panel'` there and the
  // in-flight content just carries over into the close transition unchanged.
  const contentVariant: 'panel' | 'spinner' | 'fallback' | 'success' =
    showSuccess
      ? 'success'
      : view === 'spinner' && !compact
      ? 'spinner'
      : view === 'fallback'
      ? 'fallback'
      : 'panel';

  return (
    <div
      ref={rootRef}
      className={cn(styles.root, layout === 'desktop' && styles.rootClipped)}
    >
      {layout === 'desktop' ? (
        <>
          {/* The image-darkening overlay and the trigger's static anchor spot
              only make sense for the PLP grid card's real desktop panel,
              which floats over its own image — the stacked card has no
              image to darken and anchors to the caller-provided
              `stackedAnchorRef` instead (see QuickAddPopover.Content below). */}
          <div
            className={cn(styles.overlay, open && styles.overlayVisible)}
            aria-hidden
          />
          {/* Stable positioning reference for the popover — the trigger
              transforms itself out of view once open (`.cta[data-popup-open]`),
              and anchoring to a DOM node whose own rect keeps moving drags the
              panel down with it. This sits at the trigger's same (static) spot
              but never animates, so Base UI always measures the right place. */}
          <div ref={anchorRef} className={styles.anchor} aria-hidden />
        </>
      ) : null}
      <QuickAddPopover.Root
        open={popoverOpen}
        onOpenChange={handleOpenChange}
        layout={layout}
        stackedVariant={compactVariant}
      >
        <QuickAddPopover.Trigger
          triggerRef={triggerRef}
          label={t('quickAdd.cta')}
          aria-label={
            isPendingCollapse
              ? t('quickAdd.addingAriaLabel', { title: productTitle })
              : t('quickAdd.triggerAriaLabel', { title: productTitle })
          }
          iconVariant={
            !compact
              ? 'overlay'
              : compactVariant === 'chip'
              ? 'withImage'
              : 'inline'
          }
          pending={isPendingCollapse}
        />
        <QuickAddPopover.Content
          open={popoverOpen}
          variant={contentVariant}
          aria-labelledby={showSuccess ? successLabelId : titleId}
          collisionBoundary={rootRef.current}
          anchor={
            layout === 'sheet'
              ? undefined
              : layout === 'stacked'
              ? stackedAnchorRef
              : anchorRef
          }
          availableHeight={availableHeight}
        >
          {view === 'success' ? (
            <QuickAddPopover.Success
              onClose={closeFromButton}
              labelId={successLabelId}
            />
          ) : view === 'spinner' && !compact ? (
            <>
              {/* Title stays for the popup's accessible name (aria-labelledby)
                  without showing "Select Size" over a view with nothing left
                  to select. */}
              <QuickAddPopover.Header
                titleId={titleId}
                onClose={closeFromButton}
                visuallyHidden
              />
              <QuickAddPopover.Spinner />
            </>
          ) : view === 'fallback' ? (
            <>
              <QuickAddPopover.Header
                titleId={titleId}
                onClose={closeFromButton}
              />
              <QuickAddPopover.Fallback
                message={fallbackMessage}
                restrictionMessage={restrictionMessage}
                messageVariables={addErrorContext}
                productUrl={productUrl}
              />
            </>
          ) : (
            <>
              <QuickAddPopover.Header
                titleId={titleId}
                onClose={closeFromButton}
              >
                {headerTitle}
              </QuickAddPopover.Header>
              {summary ? (
                <QuickAddPopover.ProductSummary
                  name={productTitle}
                  {...summary}
                />
              ) : null}
              {/* `view === 'spinner'` only reaches here when `compact` —
                  `isPendingCollapse` is already closing the whole popup, so
                  these are just whatever was on screen a moment ago (the
                  grid, or the skeleton for an auto-add product), carried
                  unchanged into that close transition instead of swapping to
                  new content first. */}
              {view === 'skeleton' ? (
                // A known variant count (> 1) sizes the placeholder to the
                // grid that's coming; otherwise the part's default is used.
                // `colours` is always `[]` here (resolved from `product`,
                // which is null while loading) — nothing for a colour row to
                // show yet, so it's only rendered in the branch below.
                <QuickAddPopover.Skeleton
                  cellCount={
                    variantCount && variantCount > 1 ? variantCount : undefined
                  }
                />
              ) : (
                <>
                  {/* ED-355: only when the product actually has more than one
                      colour — a single-colour product renders exactly as
                      before this row existed. */}
                  {needsColourSelection ? (
                    <QuickAddPopover.ColourRow
                      colours={colours}
                      selectedColourId={selectedColourId}
                      onColourSelect={handleColourSelect}
                    />
                  ) : null}
                  {/* Same position the success bar occupies — right under
                      the header, so a failed add is visible the instant it
                      happens instead of sitting below a grid the shopper
                      may have to scroll past. */}
                  {addStatus === 'error' ? (
                    <QuickAddPopover.Fallback
                      message={
                        addError === 'quickAdd.error.insufficientStock'
                          ? 'insufficientStock'
                          : 'error'
                      }
                      messageVariables={addErrorContext}
                      productUrl={productUrl}
                    />
                  ) : null}
                  <QuickAddPopover.SizeGrid
                    sizes={sizes}
                    selectedSizeId={selectedSizeId}
                    onSizeSelect={handleSizeSelect}
                  />
                </>
              )}
            </>
          )}
        </QuickAddPopover.Content>
      </QuickAddPopover.Root>
    </div>
  );
};

export default QuickAdd;
